import type { Dispatch, ReactNode, SetStateAction } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { ArrowRight, ChevronDown, CalendarIcon, TrendingDown, Minus, Sparkles } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Dialog, DialogContent, DialogTrigger } from '@/components/ui/dialog';
import { Calendar } from '@/components/ui/calendar';
import { Slider } from '@/components/ui/slider';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { cn, questionMistakeI18N } from '@/lib/utils';
import type { CompletedAssignment, SelfAssessmentData } from '@/pages/student/SelfAssessPage';
import { MISTAKE_OPTIONS, SOLUTION_OPTIONS } from '@/pages/student/SelfAssessPage';
import selectTaskIcon from '@/assets/student/self-assess/select-task.svg?raw';
import deadlineCalIcon from '@/assets/student/self-assess/deadline-cal.svg?raw';
import submitCalIcon from '@/assets/student/self-assess/submit-cal.svg?raw';
import snapshotIcon from '@/assets/student/self-assess/snapshot.svg?raw';
import scoreIcon from '@/assets/student/self-assess/score.svg?raw';
import timeIcon from '@/assets/student/self-assess/time.svg?raw';
import knowledgeIcon from '@/assets/student/self-assess/knowledge.svg?raw';
import trendUpIcon from '@/assets/student/self-assess/trend-up.svg?raw';
import sparkleIcon from '@/assets/student/self-assess/sparkle.svg?raw';
import MobileLandscapeDashboardShell, { useStageFit } from './mobile-landscape-dashboard-shell';

// 手机横屏(844×390)學習夥伴页主体:左欄選擇作業/作業快照卡 + 右欄互動式自我評估面板;業務邏輯/狀態留在 SelfAssessPage,本組件僅橫屏呈現

// 主色（accent）按主題：橙=#9CEA4F，其餘=var(--practice-dialog-accent)，與桌面一致。
const ACCENT = 'var(--practice-dialog-accent)';
const ACCENT_FG = 'var(--practice-dialog-accent-fg)';
const CARD_BG = '#F3F3F4';

// icon 內聯（?raw + currentColor），套在定尺寸 span 內
function Icon({ svg, size, color, w, h }: { svg: string; size?: number; color?: string; w?: number; h?: number }) {
  return (
    <span
      aria-hidden
      className="inline-flex shrink-0 [&>svg]:size-full"
      style={{ width: w ?? size, height: h ?? size, color, lineHeight: 0 }}
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
}

interface Props {
  isLoading: boolean;
  stage: 'select' | 'snapshot' | 'interactive' | 'completed';
  completedAssignments: CompletedAssignment[];
  filteredAssignments: CompletedAssignment[];
  selectedAssignment?: CompletedAssignment;
  selectedAssignmentId: string;
  setSelectedAssignmentId: (id: string) => void;
  snapshotAssignment?: CompletedAssignment;
  snapshotAssignmentId: string;
  comparison: { diff: number; improved: boolean; declined: boolean } | null;
  // 選擇其他作業 下拉 + 日期篩選
  assignmentDropdownOpen: boolean;
  setAssignmentDropdownOpen: (v: boolean) => void;
  dateFilterOpen: boolean;
  setDateFilterOpen: Dispatch<SetStateAction<boolean>>;
  dateRange: { from?: Date; to?: Date };
  setDateRange: Dispatch<SetStateAction<{ from?: Date; to?: Date }>>;
  // 互動式自我評估
  interactiveStep: number;
  setInteractiveStep: Dispatch<SetStateAction<number>>;
  expectedCorrectCount: number | null;
  setExpectedCorrectCount: Dispatch<SetStateAction<number | null>>;
  selectedMistakes: string[];
  setSelectedMistakes: Dispatch<SetStateAction<string[]>>;
  challengingKp: string;
  setChallengingKp: Dispatch<SetStateAction<string>>;
  efficiencySatisfaction: number | null;
  setEfficiencySatisfaction: Dispatch<SetStateAction<number | null>>;
  frequentSolution: string[];
  setFrequentSolution: Dispatch<SetStateAction<string[]>>;
  frequentSolutionOther: string;
  setFrequentSolutionOther: Dispatch<SetStateAction<string>>;
  selfAssessData: SelfAssessmentData;
  isSaving: boolean;
  isLoadingAiFeedback: boolean;
  // handlers
  onStartAssessment: () => void;
  onProceedInteractive: () => void;
  onContinue: () => void;
  isCurrentStepValid: () => boolean;
  onGoFreePractice: () => void;
}

// 舞台內的小尺寸 Slider / Checkbox（固定尺寸，無斷點）
const SLIDER_CLASS =
  'w-full [&_[data-slot=slider-track]]:h-2 [&_[data-slot=slider-range]]:bg-[var(--practice-dialog-accent)] [&_[data-slot=slider-thumb]]:size-4 [&_[data-slot=slider-thumb]]:border-2 [&_[data-slot=slider-thumb]]:border-[var(--practice-dialog-accent)]';
const CHECKBOX_CLASS =
  'size-4 rounded-full border-[#E6E7EB] data-[state=checked]:bg-[var(--practice-dialog-accent)] data-[state=checked]:border-[var(--practice-dialog-accent)] data-[state=checked]:text-[var(--practice-dialog-accent-fg)]';
// 手机横屏日期选择日历(紧凑版,Figma 642:5391 291×189):12px 字 / 7×28 日格 / 绿圆角 nav / 选中态绿圆 / footer 清除+確定,整块 ≤~270px 稳落 390 视口
const CALENDAR_CLASSNAMES = {
  months: 'flex flex-col',
  month: 'flex flex-col gap-1.5',
  caption: 'relative flex items-center justify-center pt-0 pb-1',
  caption_label: 'text-[12px] font-normal text-[#1A1C1D]',
  nav: 'flex items-center',
  nav_button:
    'inline-flex size-[18px] cursor-pointer items-center justify-center rounded-[5px] bg-[var(--practice-dialog-accent)] p-0 text-[var(--practice-dialog-accent-fg)] opacity-100 transition hover:brightness-95',
  nav_button_previous: 'absolute left-0',
  nav_button_next: 'absolute right-0',
  table: 'w-full border-collapse',
  head_row: 'flex',
  head_cell: 'w-7 font-normal text-[11px] text-[#1A1C1D]',
  row: 'mt-1 flex w-full',
  cell: 'relative size-7 p-0 text-center text-[12px] focus-within:relative focus-within:z-20',
  day: 'inline-flex size-7 cursor-pointer items-center justify-center rounded-full p-0 font-normal text-[12px] text-[#1A1C1D] transition hover:bg-gray-100 aria-selected:opacity-100',
  day_selected:
    'rounded-full! bg-[var(--practice-dialog-accent)] text-[var(--practice-dialog-accent-fg)] hover:bg-[var(--practice-dialog-accent)] hover:text-[var(--practice-dialog-accent-fg)] focus:bg-[var(--practice-dialog-accent)] focus:text-[var(--practice-dialog-accent-fg)]',
  day_range_start: 'day-range-start rounded-full! aria-selected:bg-[var(--practice-dialog-accent)] aria-selected:text-[var(--practice-dialog-accent-fg)]',
  day_range_end: 'day-range-end rounded-full! aria-selected:bg-[var(--practice-dialog-accent)] aria-selected:text-[var(--practice-dialog-accent-fg)]',
  day_range_middle:
    'rounded-none! bg-[color-mix(in_srgb,var(--practice-dialog-accent)_22%,white)] text-foreground aria-selected:bg-[color-mix(in_srgb,var(--practice-dialog-accent)_22%,white)] aria-selected:text-foreground',
  day_today: 'font-semibold',
  day_outside: 'text-[#C4C4C4] aria-selected:text-[#C4C4C4]',
  day_disabled: 'text-[#C4C4C4] opacity-50',
  day_hidden: 'invisible',
};

export default function MobileLandscapeSelfAssess(props: Props) {
  const {
    isLoading, stage, completedAssignments, filteredAssignments,
    selectedAssignment, selectedAssignmentId, setSelectedAssignmentId,
    snapshotAssignment, snapshotAssignmentId, comparison,
    assignmentDropdownOpen, setAssignmentDropdownOpen, dateFilterOpen, setDateFilterOpen,
    dateRange, setDateRange,
    interactiveStep, setInteractiveStep,
    expectedCorrectCount, setExpectedCorrectCount,
    selectedMistakes, setSelectedMistakes,
    challengingKp, setChallengingKp,
    efficiencySatisfaction, setEfficiencySatisfaction,
    frequentSolution, setFrequentSolution,
    frequentSolutionOther, setFrequentSolutionOther,
    selfAssessData, isSaving, isLoadingAiFeedback,
    onStartAssessment, onProceedInteractive, onContinue, isCurrentStepValid, onGoFreePractice,
  } = props;
  const { t } = useTranslation();
  // 宽屏铺满:取与舞台壳一致的 shift(见 useStageFit)。左栏两卡贴左不动,右栏互动面板(主内容)吃掉 shift 变宽。
  const { shift } = useStageFit();

  const showRightPanel = stage !== 'select';
  const showSnapshot = (stage === 'snapshot' || stage === 'interactive' || stage === 'completed') && !!snapshotAssignment;
  const rightPanelDone = stage === 'completed' || (stage === 'interactive' && interactiveStep === 7);

  // ─── 卡①：選擇作業 [206,101 310×97] ───
  const selectCard = (
    <div style={{ position: 'absolute', left: 206, top: 101, width: 310, height: 97, borderRadius: 20, background: CARD_BG }}>
      {/* 題頭：icon + 選擇作業 ……… 作業標題(右) */}
      <div style={{ position: 'absolute', left: 15, top: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
        <Icon svg={selectTaskIcon} w={10} h={12} color={ACCENT} />
        <span style={{ fontSize: 12, fontWeight: 600, color: '#1A1C1D', fontFamily: 'var(--font-sans)' }}>{t('selfAssess.selectAssignment')}</span>
      </div>
      {selectedAssignment && (
        <span style={{ position: 'absolute', right: 16, top: 13, maxWidth: 130, fontSize: 12, fontWeight: 500, color: '#1A1C1D', fontFamily: 'var(--font-sans)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {selectedAssignment.assignment.assignment.title}
        </span>
      )}

      {isLoading ? (
        <div style={{ position: 'absolute', left: 0, right: 0, top: 44, textAlign: 'center', fontSize: 11, color: '#8F8F8F' }}>{t('selfAssess.loading')}</div>
      ) : completedAssignments.length === 0 ? (
        <div style={{ position: 'absolute', left: 0, right: 0, top: 44, textAlign: 'center', fontSize: 11, color: '#8F8F8F' }}>{t('selfAssess.noCompletedAssignments')}</div>
      ) : (
        <>
          {/* 日期行 */}
          <div style={{ position: 'absolute', left: 15, top: 36, display: 'flex', alignItems: 'center', gap: 22, fontSize: 12, fontFamily: 'var(--font-sans)' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 5, color: '#8F8F8F' }}>
              <Icon svg={deadlineCalIcon} size={10} />
              {t('selfAssess.deadline')}:{selectedAssignment?.assignment.assignment.dueDate
                ? format(new Date(selectedAssignment.assignment.assignment.dueDate), t('selfAssess.dateFormatFull'))
                : t('selfAssess.noDueDateSet')}
            </span>
            {selectedAssignment?.completionDate && (
              <span style={{ display: 'flex', alignItems: 'center', gap: 5, color: '#006532' }}>
                <Icon svg={submitCalIcon} size={10} />
                {t('selfAssess.completedOn')}:{format(selectedAssignment.completionDate, t('selfAssess.dateFormatFull'))}
              </span>
            )}
          </div>

          {/* 操作行：選擇其他作業(白底邊框) + 開始自我評估(主色) */}
          <div style={{ position: 'absolute', left: 15, top: 56, display: 'flex', gap: 6 }}>
            <Popover
              open={assignmentDropdownOpen}
              onOpenChange={(open) => { if (!open) setDateFilterOpen(false); setAssignmentDropdownOpen(open); }}
            >
              <PopoverTrigger asChild>
                <button type="button" style={{ width: 136, height: 29, borderRadius: 9, background: '#FEFCF9', border: '1px solid #E5E7EB', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 10px', fontSize: 12, fontWeight: 500, color: '#000', fontFamily: 'var(--font-sans)' }} className="hover:brightness-[0.98]">
                  {t('selfAssess.selectOther')}
                  <ChevronDown style={{ width: 12, height: 12, color: '#8F8F8F' }} />
                </button>
              </PopoverTrigger>
              {/* 手机横屏下拉:高度自适应视口(header 钉顶 + 列表内滚),避免在 390 高被裁。portal 到 body 按真实视口渲染 */}
              <PopoverContent
                className="flex max-h-[var(--radix-popover-content-available-height)] w-[280px] flex-col overflow-hidden rounded-[12px]! border border-[#EFEFEF] p-0 shadow-[0_8px_28px_-6px_rgba(0,0,0,0.18)]"
                align="start"
                collisionPadding={12}
                onInteractOutside={(e) => { if (dateFilterOpen) e.preventDefault(); }}
                style={{ fontFamily: 'var(--font-sans)' }}
              >
                <div className="flex shrink-0 items-center justify-between border-b border-[#E6E7EB] px-3.5 py-2.5">
                  <span className="text-[12px] text-[#1A1C1D]" style={{ fontWeight: 450 }}>{t('selfAssess.dateFilter')}</span>
                  {/* 日期选择:手机横屏改居中弹窗(日历不能内滚,锚定在 390 高必裁;居中 Dialog 恒可容纳,同學習總覽日历弹窗套路) */}
                  <Dialog open={dateFilterOpen} onOpenChange={setDateFilterOpen}>
                    <DialogTrigger asChild>
                      <button
                        type="button"
                        className={cn('flex h-7 cursor-pointer items-center gap-1.5 rounded-[8px] px-2 text-[12px] transition hover:bg-gray-100', (dateRange.from || dateRange.to) ? 'text-[#1A1C1D]' : 'text-[#8F8F8F]')}
                        style={{ fontWeight: 450 }}
                      >
                        <CalendarIcon className="size-3.5" />
                        {dateRange.from ? (dateRange.to ? <>{format(dateRange.from, 'MM/dd')} - {format(dateRange.to, 'MM/dd')}</> : format(dateRange.from, 'MM/dd')) : t('selfAssess.allDates')}
                      </button>
                    </DialogTrigger>
                    <DialogContent
                      showCloseButton={false}
                      showTitle={false}
                      className="w-auto! max-w-[calc(100vw-3rem)]! rounded-[16px]! border-0! bg-white! p-4! gap-0! shadow-[0_10px_40px_-8px_rgba(0,0,0,0.25)]"
                      style={{ fontFamily: 'var(--font-sans)' }}
                    >
                      <Calendar
                        mode="range"
                        selected={{ from: dateRange.from, to: dateRange.to }}
                        onSelect={(range) => setDateRange({ from: range?.from, to: range?.to })}
                        numberOfMonths={1}
                        className="p-0"
                        classNames={CALENDAR_CLASSNAMES}
                      />
                      <div className="mt-3 flex gap-2">
                        <button type="button" onClick={() => setDateRange({})} className="h-[30px] flex-1 cursor-pointer rounded-[8px] border border-[#E6E7EB] bg-white text-[12px] text-[#1A1C1D] transition hover:bg-gray-50" style={{ fontWeight: 450 }}>{t('selfAssess.clearFilter')}</button>
                        <button type="button" onClick={() => setDateFilterOpen(false)} className="h-[30px] flex-1 cursor-pointer rounded-[8px] bg-[var(--practice-dialog-accent)] text-[12px] text-[var(--practice-dialog-accent-fg)] transition hover:brightness-95" style={{ fontWeight: 500 }}>{t('component.confirm')}</button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
                <div className="sa-scroll min-h-0 flex-1 overflow-y-auto p-2">
                  {filteredAssignments.length === 0 ? (
                    <div className="p-4 text-center text-[12px] text-[#8F8F8F]">{t('selfAssess.noAssignmentsInRange')}</div>
                  ) : (
                    filteredAssignments.map((a) => {
                      const isSel = selectedAssignmentId === a.assignment.assignment.id?.toString();
                      return (
                        <div
                          key={a.assignment.assignment.id}
                          onClick={() => { setSelectedAssignmentId(a.assignment.assignment.id?.toString() || ''); setAssignmentDropdownOpen(false); }}
                          className={cn('cursor-pointer rounded-[8px] px-3 py-2.5 transition-colors', isSel ? 'bg-[color-mix(in_srgb,var(--practice-dialog-accent)_30%,white)]' : 'hover:bg-gray-50')}
                        >
                          <div className="text-[12px] text-[#1A1C1D]" style={{ fontWeight: 500 }}>{a.assignment.assignment.title}</div>
                          <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[12px]" style={{ fontWeight: 450 }}>
                            {a.completionDate && <span className="text-[#006532]">{t('selfAssess.completedOn')}:{format(a.completionDate, 'yyyy年MM月dd日')}</span>}
                            <span className="text-[#8F8F8F]">{t('selfAssess.deadline')}:{a.assignment.assignment.dueDate ? format(new Date(a.assignment.assignment.dueDate), 'yyyy年MM月dd日') : t('selfAssess.notSet')}</span>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </PopoverContent>
            </Popover>

            <button
              type="button"
              onClick={onStartAssessment}
              disabled={!selectedAssignmentId || (stage !== 'select' && selectedAssignmentId === snapshotAssignmentId)}
              style={{ width: 136, height: 29, borderRadius: 9, background: ACCENT, color: ACCENT_FG, border: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 12px', fontSize: 12, fontWeight: 500, fontFamily: 'var(--font-sans)' }}
              className="hover:brightness-95 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {t('selfAssess.startAssessment')}
              <ArrowRight style={{ width: 12, height: 9 }} />
            </button>
          </div>
        </>
      )}
    </div>
  );

  // ─── 卡②：作業快照 [206,206 310×172] ───
  const scorePct = snapshotAssignment
    ? Math.round((snapshotAssignment.totalCorrect / Math.max(1, snapshotAssignment.totalQuestions)) * 100)
    : 0;
  const durationText = snapshotAssignment
    ? (snapshotAssignment.totalDuration >= 60
      ? `${Math.floor(snapshotAssignment.totalDuration / 60)}${t('selfAssess.minutes')}${snapshotAssignment.totalDuration % 60}${t('selfAssess.seconds')}`
      : `${snapshotAssignment.totalDuration}${t('selfAssess.seconds')}`)
    : '-';
  const kpTotal = snapshotAssignment?.knowledgePoints?.length ?? 0;
  const kpMistaked = snapshotAssignment?.mistakedKnowledgePoints?.length ?? 0;
  const kpMastered = Math.max(0, kpTotal - kpMistaked);

  // 統計色塊：[bg, labelColor, icon, label, valueNode]（2×2）
  const cell = (
    left: number, top: number, bg: string, labelColor: string, icon: ReactNode, label: string, value: ReactNode,
  ) => (
    <div style={{ position: 'absolute', left, top, width: 136, height: 59, borderRadius: 9, background: bg, padding: '9px 13px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 500, color: labelColor, fontFamily: 'var(--font-sans)' }}>
        {icon}
        <span>{label}</span>
      </div>
      <div style={{ fontSize: 14, fontWeight: 700, color: '#000', fontFamily: 'var(--font-sans)' }}>{value}</div>
    </div>
  );

  const snapshotCard = showSnapshot && snapshotAssignment && (
    <div key={snapshotAssignmentId} style={{ position: 'absolute', left: 206, top: 206, width: 310, height: 172, borderRadius: 20, background: CARD_BG }} className="animate-in fade-in slide-in-from-bottom-2 duration-300">
      {/* 題頭 */}
      <div style={{ position: 'absolute', left: 15, top: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
        <Icon svg={snapshotIcon} size={12} color={ACCENT} />
        <span style={{ fontSize: 12, fontWeight: 600, color: '#1A1C1D', fontFamily: 'var(--font-sans)' }}>{t('selfAssess.snapshot')}</span>
      </div>
      <span style={{ position: 'absolute', right: 16, top: 13, maxWidth: 130, fontSize: 12, fontWeight: 500, color: '#1A1C1D', fontFamily: 'var(--font-sans)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {snapshotAssignment.assignment.assignment.title}
      </span>

      {/* 2×2 統計色塊 */}
      {cell(16, 36, '#B6E2EF', '#82A3AD', <Icon svg={scoreIcon} size={9} />, t('selfAssess.score'),
        <>{snapshotAssignment.totalCorrect}/{snapshotAssignment.totalQuestions} ({scorePct}%)</>)}
      {cell(158, 36, '#F8EFA8', '#DAC62E', <Icon svg={timeIcon} size={9} />, t('selfAssess.completionTime'), durationText)}
      {cell(16, 101, '#F8D1C3', '#A6887E', <Icon svg={knowledgeIcon} size={9} />, t('selfAssess.knowledgePoints'),
        kpTotal === 0 ? '-' : `${kpMastered}/${kpTotal} (${Math.round((kpMastered / kpTotal) * 100)}%)`)}
      {cell(158, 101, '#E5F8E4', '#A3BDA2',
        comparison?.improved ? <Icon svg={trendUpIcon} w={14} h={9} /> : comparison?.declined ? <TrendingDown style={{ width: 12, height: 12 }} /> : <Minus style={{ width: 12, height: 12 }} />,
        t('selfAssess.comparisonTitle'),
        <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 5 }}>
          <span>{comparison ? `${comparison.diff > 0 ? '+' : ''}${comparison.diff.toFixed(0)}%` : '-'}</span>
          {comparison && (
            <span style={{ fontSize: 12, fontWeight: 400 }}>
              {comparison.improved ? t('selfAssess.improved') : comparison.declined ? t('selfAssess.declined') : t('selfAssess.stable')}
            </span>
          )}
        </span>)}
    </div>
  );

  // ─── 繼續/完成 按鈕 ───
  const continueButton = (
    <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: 4 }}>
      <button
        type="button"
        onClick={onContinue}
        disabled={isSaving || isLoadingAiFeedback || !isCurrentStepValid()}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, borderRadius: 12, padding: '7px 16px', fontSize: 12, fontWeight: 600, background: ACCENT, color: ACCENT_FG, border: 0, fontFamily: 'var(--font-sans)' }}
        className="cursor-pointer transition hover:brightness-95 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {isLoadingAiFeedback ? (
          <><span className="size-3 animate-spin rounded-full border-2 border-current border-t-transparent" />{t('selfAssess.loadingAiFeedback')}</>
        ) : isSaving ? t('common.loading') : (
          <>{interactiveStep === 6 ? t('selfAssess.finishButton') : t('selfAssess.continueButton')}<ArrowRight style={{ width: 13, height: 13 }} /></>
        )}
      </button>
    </div>
  );

  // ─── 右欄面板內容（依 stage/step） ───
  const qTitle = (s: string) => <h3 style={{ margin: 0, fontSize: 13, fontWeight: 500, color: '#1A1C1D', fontFamily: 'var(--font-sans)' }}>{s}</h3>;
  const qDesc = (s: string) => <p style={{ margin: 0, fontSize: 11, color: '#8F8F8F', fontFamily: 'var(--font-sans)' }}>{s}</p>;

  const rightContent = (
    <>
      {/* snapshot：居中「準備好…」提示 */}
      {stage === 'snapshot' && (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, textAlign: 'center', padding: '0 12px' }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, lineHeight: 1.35, color: '#1A1C1D', fontFamily: 'var(--font-sans)' }}>{t('selfAssess.interactivePromptTitle')}</h3>
          <p style={{ margin: 0, maxWidth: 266, fontSize: 12, lineHeight: 1.5, color: '#1A1C1D', fontFamily: 'var(--font-sans)' }}>{t('selfAssess.interactivePromptDesc')}</p>
          <button type="button" onClick={onProceedInteractive} style={{ marginTop: 4, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8, width: 152, height: 29, borderRadius: 9, background: ACCENT, color: ACCENT_FG, border: 0, cursor: 'pointer', fontSize: 12, fontWeight: 500, fontFamily: 'var(--font-sans)' }} className="transition hover:brightness-95">
            {t('selfAssess.startInteractive')}
            <ArrowRight style={{ width: 12, height: 9 }} />
          </button>
        </div>
      )}

      {/* interactive Q1~Q7 */}
      {stage === 'interactive' && snapshotAssignment && (
        <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {/* 题目内容局部滚动(标题/按钮固定) */}
          <div className="sa-scroll" style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', display: 'flex', flexDirection: 'column', gap: 10, paddingRight: 4 }}>
          {interactiveStep === 1 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {qTitle(t('selfAssess.q1Title'))}
              {qDesc(t('selfAssess.q1Desc'))}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '4px 0' }} onPointerDown={() => expectedCorrectCount === null && setExpectedCorrectCount(0)}>
                <Slider value={[expectedCorrectCount ?? 0]} onValueChange={(v) => setExpectedCorrectCount(v[0])} min={0} max={snapshotAssignment.totalQuestions} step={1} className={SLIDER_CLASS} />
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#8F8F8F' }}><span>0</span><span>{snapshotAssignment.totalQuestions}</span></div>
                <div style={{ borderRadius: 14, border: '1px solid #E5E7EB', background: '#fff', padding: '8px 12px', textAlign: 'center' }}>
                  {expectedCorrectCount === null
                    ? <span style={{ fontSize: 12, color: '#9CA3AF' }}>{t('selfAssess.scaleNotSelected')}</span>
                    : <span style={{ fontSize: 12, fontWeight: 600, color: '#1A1C1D' }}>{t('selfAssess.q1Format', { x: expectedCorrectCount, y: snapshotAssignment.totalQuestions, percent: Math.round((expectedCorrectCount / Math.max(1, snapshotAssignment.totalQuestions)) * 100) })}</span>}
                </div>
              </div>
            </div>
          )}

          {interactiveStep === 2 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {qTitle(t('selfAssess.q2Title'))}
              {qDesc(t('selfAssess.q2Hint'))}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {(snapshotAssignment.knowledgePoints && snapshotAssignment.knowledgePoints.length > 0 ? snapshotAssignment.knowledgePoints : snapshotAssignment.assignment.topicList).map((kp, idx) => (
                  <button key={idx} type="button" onClick={() => setChallengingKp((prev) => (prev ? `${prev}, ${kp}` : kp))} className="cursor-pointer rounded-[14px] border border-[color-mix(in_srgb,var(--practice-dialog-accent)_55%,transparent)] bg-[color-mix(in_srgb,var(--practice-dialog-accent)_18%,white)] px-2.5 py-1 text-[11px] text-foreground transition hover:brightness-95">{kp}</button>
                ))}
              </div>
              <Textarea value={challengingKp} onChange={(e) => setChallengingKp(e.target.value)} placeholder={t('selfAssess.q2Placeholder')} className="min-h-[80px] rounded-[14px] border-gray-200 bg-white text-xs" />
            </div>
          )}

          {interactiveStep === 3 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {qTitle(t('selfAssess.q3Title'))}
              {qDesc(t('selfAssess.q3Hint'))}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {MISTAKE_OPTIONS.map((option) => {
                  const checked = selectedMistakes.includes(option.key);
                  return (
                    <label key={option.key} className={cn('flex cursor-pointer items-start gap-2.5 rounded-[14px] border p-2.5 transition', checked ? 'border-[var(--practice-dialog-accent)] bg-[color-mix(in_srgb,var(--practice-dialog-accent)_15%,white)]' : 'border-gray-200 hover:border-gray-300')}>
                      <Checkbox className={cn('mt-0.5', CHECKBOX_CLASS)} checked={checked} onCheckedChange={(c) => { if (c) setSelectedMistakes((p) => [...p, option.key]); else setSelectedMistakes((p) => p.filter((m) => m !== option.key)); }} />
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ fontSize: 12, fontWeight: 500, color: '#1A1C1D' }}>{t(`selfAssess.${option.translationKey}`)}</div>
                        <div style={{ fontSize: 10.5, color: '#8F8F8F' }}>{t(`selfAssess.${option.explanationKey}`)}</div>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          {(interactiveStep === 4 || interactiveStep === 7) && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {qTitle(t('selfAssess.phaseSummaryTitle'))}
              <div style={{ borderRadius: 14, border: '1px solid #E5E7EB', overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                  <thead>
                    <tr style={{ background: '#F9FAFB', textAlign: 'left', color: '#374151' }}>
                      <th style={{ padding: 8, fontWeight: 500 }}>{t('selfAssess.phaseSummaryQuestion')}</th>
                      <th style={{ padding: 8, fontWeight: 500 }}>{t('selfAssess.phaseSummaryYourAnswer')}</th>
                      {interactiveStep === 4 && <th style={{ padding: 8, fontWeight: 500 }}>{t('selfAssess.phaseSummaryActual')}</th>}
                    </tr>
                  </thead>
                  <tbody style={{ color: '#1A1C1D' }}>
                    <tr style={{ borderTop: '1px solid #E5E7EB' }}>
                      <td style={{ padding: 8, verticalAlign: 'top' }}>{t('selfAssess.q1Title')}</td>
                      <td style={{ padding: 8, verticalAlign: 'top' }}>{selfAssessData.expected_correctness !== undefined ? `${Math.round(selfAssessData.expected_correctness * 100)}%` : '-'}</td>
                      {interactiveStep === 4 && <td style={{ padding: 8, verticalAlign: 'top' }}>{snapshotAssignment.totalQuestions > 0 ? `${Math.round((snapshotAssignment.totalCorrect / snapshotAssignment.totalQuestions) * 100)}%` : '-'}</td>}
                    </tr>
                    <tr style={{ borderTop: '1px solid #E5E7EB' }}>
                      <td style={{ padding: 8, verticalAlign: 'top' }}>{t('selfAssess.q2Title')}</td>
                      <td style={{ padding: 8, verticalAlign: 'top' }}>{selfAssessData.challenging_kp || '-'}</td>
                      {interactiveStep === 4 && <td style={{ padding: 8, verticalAlign: 'top' }}>{snapshotAssignment.mistakedKnowledgePoints && snapshotAssignment.mistakedKnowledgePoints.length > 0 ? snapshotAssignment.mistakedKnowledgePoints.slice(0, 3).join(', ') : t('selfAssess.noMistakedKpMessage')}</td>}
                    </tr>
                    <tr style={{ borderTop: '1px solid #E5E7EB' }}>
                      <td style={{ padding: 8, verticalAlign: 'top' }}>{t('selfAssess.q3Title')}</td>
                      <td style={{ padding: 8, verticalAlign: 'top' }}>{selfAssessData.primary_mistakes && selfAssessData.primary_mistakes.length > 0 ? selfAssessData.primary_mistakes.map((m) => { const o = MISTAKE_OPTIONS.find((opt) => opt.key === m); return o ? t(`selfAssess.${o.translationKey}`) : m; }).join(', ') : Array.isArray(selfAssessData.primary_mistakes) ? t('selfAssess.noMistakes') : '-'}</td>
                      {interactiveStep === 4 && <td style={{ padding: 8, verticalAlign: 'top' }}>{snapshotAssignment.primaryMistakes && snapshotAssignment.primaryMistakes.length > 0 ? questionMistakeI18N(t, snapshotAssignment.primaryMistakes[0]) : Array.isArray(snapshotAssignment.primaryMistakes) ? t('selfAssess.noMistakes') : t('selfAssess.phaseSummaryPlaceholder')}</td>}
                    </tr>
                    {interactiveStep === 7 && (
                      <>
                        <tr style={{ borderTop: '1px solid #E5E7EB' }}>
                          <td style={{ padding: 8, verticalAlign: 'top' }}>{t('selfAssess.q4Title')}</td>
                          <td style={{ padding: 8, verticalAlign: 'top' }}>{selfAssessData.efficiency_satisfaction ? `${selfAssessData.efficiency_satisfaction}/5` : '-'}</td>
                        </tr>
                        <tr style={{ borderTop: '1px solid #E5E7EB' }}>
                          <td style={{ padding: 8, verticalAlign: 'top' }}>{t('selfAssess.q5Title')}</td>
                          <td style={{ padding: 8, verticalAlign: 'top' }}>{selfAssessData.frequent_solution && selfAssessData.frequent_solution.length > 0 ? selfAssessData.frequent_solution.join(', ') : '-'}</td>
                        </tr>
                      </>
                    )}
                  </tbody>
                </table>
              </div>

              {interactiveStep === 7 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, borderRadius: 16, padding: 12, backgroundColor: 'color-mix(in srgb, var(--practice-dialog-accent) 18%, white)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#1A1C1D' }}>
                    <Icon svg={sparkleIcon} size={13} color={ACCENT} />
                    <span style={{ fontSize: 12, fontWeight: 600 }}>{t('selfAssess.aiInsightsTitle')}</span>
                  </div>
                  {isLoadingAiFeedback ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#4B5563' }}>
                      <span className="size-3 animate-spin rounded-full border-2 border-current border-t-transparent" />
                      <span>{t('selfAssess.loadingAiFeedback')}</span>
                    </div>
                  ) : (
                    <p style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: 12, color: 'rgba(26,28,29,0.8)' }}>{selfAssessData.ai_feedback || t('selfAssess.aiInsightsFallback')}</p>
                  )}
                </div>
              )}
            </div>
          )}

          {interactiveStep === 5 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {qTitle(t('selfAssess.q4Title'))}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '4px 0' }} onPointerDown={() => efficiencySatisfaction === null && setEfficiencySatisfaction(3)}>
                <Slider value={[efficiencySatisfaction ?? 3]} onValueChange={(v) => setEfficiencySatisfaction(v[0])} min={1} max={5} step={1} className={SLIDER_CLASS} />
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#8F8F8F' }}>
                  <span>{t('selfAssess.q4Level1')}</span><span>{t('selfAssess.q4Level3')}</span><span>{t('selfAssess.q4Level5')}</span>
                </div>
                <div style={{ borderRadius: 14, border: '1px solid #E5E7EB', background: '#fff', padding: '8px 12px', textAlign: 'center' }}>
                  {efficiencySatisfaction === null
                    ? <span style={{ fontSize: 12, color: '#9CA3AF' }}>{t('selfAssess.scaleNotSelected')}</span>
                    : <span style={{ fontSize: 12, fontWeight: 600, color: '#1A1C1D' }}>{efficiencySatisfaction} / 5 — {t(`selfAssess.q4Level${efficiencySatisfaction}`)}</span>}
                </div>
              </div>
            </div>
          )}

          {interactiveStep === 6 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {qTitle(t('selfAssess.q5Title'))}
              {qDesc(t('selfAssess.q5Hint'))}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {SOLUTION_OPTIONS.map((option) => {
                  const optionText = `${option.key}. ${t(`selfAssess.${option.translationKey}`)}`;
                  const isSel = frequentSolution.some((s) => s.startsWith(`${option.key}.`));
                  return (
                    <label key={option.key} className={cn('flex cursor-pointer items-center gap-2.5 rounded-[14px] border p-2.5 transition', isSel ? 'border-[var(--practice-dialog-accent)] bg-[color-mix(in_srgb,var(--practice-dialog-accent)_15%,white)]' : 'border-gray-200 hover:border-gray-300')}>
                      <Checkbox className={CHECKBOX_CLASS} checked={isSel} onCheckedChange={(c) => { if (c) setFrequentSolution((p) => [...p, optionText]); else { setFrequentSolution((p) => p.filter((s) => !s.startsWith(`${option.key}.`))); if (option.key === 'F') setFrequentSolutionOther(''); } }} />
                      <span style={{ fontSize: 12, fontWeight: 500, color: '#1A1C1D' }}>{t(`selfAssess.${option.translationKey}`)}</span>
                    </label>
                  );
                })}
              </div>
              {frequentSolution.some((s) => s.startsWith('F.')) && (
                <Textarea value={frequentSolutionOther} onChange={(e) => setFrequentSolutionOther(e.target.value)} placeholder={t('selfAssess.q5OtherPlaceholder')} className="min-h-[60px] rounded-[14px] border-gray-200 bg-white text-xs" />
              )}
            </div>
          )}

          </div>
          {interactiveStep <= 6 && continueButton}
          {interactiveStep === 7 && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: 4 }}>
              <button type="button" onClick={onGoFreePractice} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, borderRadius: 12, padding: '7px 16px', fontSize: 12, fontWeight: 600, background: ACCENT, color: ACCENT_FG, border: 0, cursor: 'pointer', fontFamily: 'var(--font-sans)' }} className="transition hover:brightness-95">
                {t('selfAssess.goFreePractice')}
              </button>
            </div>
          )}
        </div>
      )}

      {/* completed */}
      {stage === 'completed' && snapshotAssignment && (
        <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {interactiveStep !== 7 && (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10, textAlign: 'center', padding: '0 8px' }}>
              <h3 style={{ margin: 0, whiteSpace: 'pre-line', fontSize: 16, fontWeight: 600, lineHeight: 1.35, color: '#1A1C1D', fontFamily: 'var(--font-sans)' }}>{t('selfAssess.completedMessage')}</h3>
              <p style={{ margin: 0, maxWidth: 266, fontSize: 12, color: '#8F8F8F', fontFamily: 'var(--font-sans)' }}>{t('selfAssess.completedDesc')}</p>
              <button type="button" onClick={() => setInteractiveStep(7)} style={{ marginTop: 4, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8, height: 29, padding: '0 18px', borderRadius: 9, background: ACCENT, color: ACCENT_FG, border: 0, cursor: 'pointer', fontSize: 12, fontWeight: 500, fontFamily: 'var(--font-sans)' }} className="transition hover:brightness-95">
                {t('selfAssess.revisitFeedback')}
                <Sparkles style={{ width: 13, height: 13 }} fill="currentColor" />
              </button>
            </div>
          )}
          {interactiveStep === 7 && (
            <>
              <div className="sa-scroll" style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', paddingRight: 4 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, borderRadius: 20, padding: 12, backgroundColor: 'color-mix(in srgb, var(--practice-dialog-accent) 18%, white)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#1A1C1D' }}>
                  <Icon svg={sparkleIcon} size={13} color={ACCENT} />
                  <span style={{ fontSize: 12, fontWeight: 600 }}>{t('selfAssess.aiInsightsTitle')}</span>
                </div>
                <p style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: 12, color: 'rgba(26,28,29,0.8)' }}>{selfAssessData.ai_feedback || t('selfAssess.aiInsightsPlaceholder')}</p>
              </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: 4 }}>
                <button type="button" onClick={onGoFreePractice} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, borderRadius: 12, padding: '7px 16px', fontSize: 12, fontWeight: 600, background: ACCENT, color: ACCENT_FG, border: 0, cursor: 'pointer', fontFamily: 'var(--font-sans)' }} className="transition hover:brightness-95">
                  {t('selfAssess.goFreePractice')}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );

  // ─── 右欄面板 [523,101 310×277] ───
  const rightPanel = showRightPanel && (
    <div
      className="animate-in fade-in duration-300"
      style={{ position: 'absolute', left: 523, top: 101, width: 310 + shift, height: 277, borderRadius: 20, background: CARD_BG, overflow: 'hidden', padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}
    >
      {/* interactive/completed 態頂部小題頭（snapshot 態的居中提示自帶標題，不重複） */}
      {(stage === 'interactive' || stage === 'completed') && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#1A1C1D', flexShrink: 0 }}>
          <Icon svg={sparkleIcon} size={14} color={ACCENT} />
          <span style={{ fontSize: 13, fontWeight: 600, fontFamily: 'var(--font-sans)' }}>
            {rightPanelDone ? t('selfAssess.completedTitle') : t('selfAssess.interactiveSelfAssessment')}
          </span>
        </div>
      )}
      {rightContent}
    </div>
  );

  const content = (
    <>
      {/* 頁面標題 */}
      <h1 style={{ position: 'absolute', left: 204, top: 71, margin: 0, fontSize: 20, fontWeight: 520, color: '#000', fontFamily: 'var(--font-sans)', lineHeight: '20px' }}>
        {t('student.aibot')}
      </h1>
      {selectCard}
      {snapshotCard}
      {rightPanel}
    </>
  );

  return createPortal(
    <div className="hidden [@media(max-height:500px)]:flex fixed inset-0 items-center justify-center overflow-hidden" style={{ background: 'var(--practice-page-bg)' }}>
      <MobileLandscapeDashboardShell>{content}</MobileLandscapeDashboardShell>
    </div>,
    document.body,
  );
}
